/** Keep the day counts in sync with `packages/backend/src/utils/followupPriority.ts`. */
export type FollowupPriority = 'urgent' | 'high' | 'medium' | 'low';

export interface FollowupPriorityChoice {
  value: FollowupPriority;
  label: string;
  days: number;
  activeClass: string;
  ring: string;
}

export const FOLLOWUP_PRIORITY_CHOICES: readonly FollowupPriorityChoice[] = [
  {
    value: 'urgent',
    label: 'URGENT',
    days: 2,
    activeClass: 'bg-fuchsia-700 hover:bg-fuchsia-800',
    ring: 'ring-fuchsia-300',
  },
  {
    value: 'high',
    label: 'HIGH',
    days: 7,
    activeClass: 'bg-red-600 hover:bg-red-700',
    ring: 'ring-red-300',
  },
  {
    value: 'medium',
    label: 'MEDIUM',
    days: 14,
    activeClass: 'bg-orange-500 hover:bg-orange-600',
    ring: 'ring-orange-300',
  },
  {
    value: 'low',
    label: 'LOW',
    days: 30,
    activeClass: 'bg-blue-500 hover:bg-blue-600',
    ring: 'ring-blue-300',
  },
];

export function followupDueDays(priority: string | null | undefined): number {
  return FOLLOWUP_PRIORITY_CHOICES.find((choice) => choice.value === priority)?.days ?? 30;
}

export function followupSolidClass(priority: string | null | undefined): string {
  if (priority === 'urgent') return 'bg-fuchsia-700 text-white';
  if (priority === 'high') return 'bg-red-600 text-white';
  if (priority === 'medium') return 'bg-orange-600 text-white';
  return 'bg-blue-600 text-white';
}

export function followupSoftClass(priority: string | null | undefined): string {
  if (priority === 'urgent') return 'bg-fuchsia-50 border-fuchsia-300 text-fuchsia-800';
  if (priority === 'high') return 'bg-red-50 border-red-200 text-red-700';
  if (priority === 'medium') return 'bg-orange-50 border-orange-200 text-orange-700';
  return 'bg-blue-50 border-blue-200 text-blue-700';
}

export function followupPanelClass(priority: string | null | undefined): string {
  if (priority === 'urgent') return 'bg-fuchsia-50 border-fuchsia-300';
  if (priority === 'high') return 'bg-red-50 border-red-300';
  if (priority === 'medium') return 'bg-orange-50 border-orange-300';
  return 'bg-blue-50 border-blue-300';
}

export function followupIconClass(priority: string | null | undefined): string {
  if (priority === 'urgent') return 'text-fuchsia-700';
  if (priority === 'high') return 'text-red-600';
  if (priority === 'medium') return 'text-orange-600';
  return 'text-blue-600';
}

export const INTEREST_NOTE_PREFIX = 'Ernsthaftes Interesse:';

export interface InterestBaseline {
  needsFollowup: boolean;
  priority: Exclude<FollowupPriority, 'urgent'>;
}

export interface InterestToggleInput {
  pressed: boolean;
  needsFollowup: boolean;
  priority: FollowupPriority;
  notes: string;
  productNames: string[];
  baseline: InterestBaseline | null;
}

export interface InterestToggleResult {
  pressed: boolean;
  needsFollowup: boolean;
  priority: FollowupPriority;
  notes: string;
  baseline: InterestBaseline | null;
}

function withoutInterestNote(notes: string): string {
  return notes
    .split('\n')
    .filter((line) => !line.trim().startsWith(INTEREST_NOTE_PREFIX))
    .join('\n')
    .trim();
}

/** Second tap restores the follow-up that was set before the interest button. */
export function toggleSeriousInterest(input: InterestToggleInput): InterestToggleResult {
  if (input.pressed) {
    const baseline = input.baseline;
    return {
      pressed: false,
      needsFollowup: baseline ? baseline.needsFollowup : false,
      priority: baseline ? baseline.priority : 'high',
      notes: withoutInterestNote(input.notes),
      baseline: null,
    };
  }

  const previous: InterestBaseline['priority'] =
    input.priority === 'urgent' ? 'high' : input.priority;
  const note = `${INTEREST_NOTE_PREFIX} ${input.productNames.join(', ')}`;
  const notes = input.notes.includes(INTEREST_NOTE_PREFIX)
    ? input.notes
    : input.notes.trim()
      ? `${input.notes.trim()}\n${note}`
      : note;

  return {
    pressed: true,
    needsFollowup: true,
    priority: 'urgent',
    notes,
    baseline: input.baseline ?? {
      needsFollowup: input.needsFollowup,
      priority: previous,
    },
  };
}
